import React, { useState, useEffect, useRef } from 'react';
import { MapPinIcon, MagnifyingGlassIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

const GeofenceMapPicker = ({ 
  initialLocation = { lat: 51.505, lng: -0.09 }, 
  initialRadius = 100,
  onLocationChange,
  onRadiusChange,
  disabled = false 
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(initialLocation);
  const [currentRadius, setCurrentRadius] = useState(initialRadius);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Load Leaflet CSS and JS
  useEffect(() => {
    const loadLeaflet = async () => {
      // Load CSS
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        css.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        css.crossOrigin = '';
        document.head.appendChild(css);
      }

      // Load JS
      if (!window.L) {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
          script.crossOrigin = '';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
    };

    loadLeaflet().then(() => {
      initializeMap();
    }).catch(error => {
      console.error('Error loading Leaflet:', error);
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }
    };
  }, []);

  const initializeMap = () => {
    if (!window.L || !mapRef.current || mapInstanceRef.current) return;

    // Initialize map
    const map = window.L.map(mapRef.current).setView([currentLocation.lat, currentLocation.lng], 15);

    // Add OpenStreetMap tiles
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    // Add marker
    const customIcon = window.L.divIcon({
      className: 'custom-map-marker',
      html: `<div style="
        width: 30px;
        height: 30px;
        background: #DC2626;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
        "></div>
      </div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });

    const marker = window.L.marker([currentLocation.lat, currentLocation.lng], {
      icon: customIcon,
      draggable: !disabled
    }).addTo(map);

    // Add circle for geofence
    const circle = window.L.circle([currentLocation.lat, currentLocation.lng], {
      color: '#DC2626',
      fillColor: '#FEE2E2',
      fillOpacity: 0.2,
      radius: currentRadius,
      weight: 2
    }).addTo(map);

    // Handle marker drag
    if (!disabled) {
      marker.on('dragend', (e) => {
        const position = e.target.getLatLng();
        const newLocation = { lat: position.lat, lng: position.lng };
        setCurrentLocation(newLocation);
        circle.setLatLng(position);
        if (onLocationChange) {
          onLocationChange(newLocation);
        }
      });

      // Handle map click
      map.on('click', (e) => {
        const newLocation = { lat: e.latlng.lat, lng: e.latlng.lng };
        setCurrentLocation(newLocation);
        marker.setLatLng(e.latlng);
        circle.setLatLng(e.latlng);
        if (onLocationChange) {
          onLocationChange(newLocation);
        }
      });
    }

    mapInstanceRef.current = map;
    markerRef.current = marker;
    circleRef.current = circle;
    setIsMapReady(true);
  };

  // Update radius when prop changes
  useEffect(() => {
    if (circleRef.current && currentRadius !== initialRadius) {
      setCurrentRadius(initialRadius);
      circleRef.current.setRadius(initialRadius);
    }
  }, [initialRadius]);

  // Update location when prop changes
  useEffect(() => {
    if (markerRef.current && circleRef.current) {
      const newPos = [initialLocation.lat, initialLocation.lng];
      markerRef.current.setLatLng(newPos);
      circleRef.current.setLatLng(newPos);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setView(newPos, mapInstanceRef.current.getZoom());
      }
      setCurrentLocation(initialLocation);
    }
  }, [initialLocation.lat, initialLocation.lng]);

  const handleRadiusChange = (newRadius) => {
    setCurrentRadius(newRadius);
    if (circleRef.current) {
      circleRef.current.setRadius(newRadius);
    }
    if (onRadiusChange) {
      onRadiusChange(newRadius);
    }
  };

  // Address search functionality
  const searchAddress = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`
      );
      const results = await response.json();
      
      const formattedResults = results.map(result => ({
        id: result.place_id,
        displayName: result.display_name,
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        address: {
          street: result.address?.road || result.address?.pedestrian || '',
          city: result.address?.city || result.address?.town || result.address?.village || '',
          state: result.address?.state || '',
          country: result.address?.country || '',
          postcode: result.address?.postcode || ''
        }
      }));

      setSearchResults(formattedResults);
    } catch (error) {
      console.error('Address search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(query);
    }, 300);
  };

  const selectSearchResult = (result) => {
    const newLocation = { lat: result.lat, lng: result.lng };
    setCurrentLocation(newLocation);
    setSearchQuery(result.displayName);
    setSearchResults([]);

    if (markerRef.current && circleRef.current && mapInstanceRef.current) {
      const newPos = [result.lat, result.lng];
      markerRef.current.setLatLng(newPos);
      circleRef.current.setLatLng(newPos);
      mapInstanceRef.current.setView(newPos, 15);
    }

    if (onLocationChange) {
      onLocationChange(newLocation, result.address);
    }
  };

  return (
    <div className="space-y-4">
      {/* Address Search */}
      <div className="relative">
        <label className="block text-sm font-medium text-dark-200 mb-2">
          Search Address
        </label>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dark-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search for an address..."
            disabled={disabled}
            className="w-full pl-10 pr-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-dark-800 disabled:text-dark-400"
          />
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-dark-700 border border-dark-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map((result) => (
              <button
                key={result.id}
                onClick={() => selectSearchResult(result)}
                className="w-full text-left px-4 py-3 hover:bg-dark-600 border-b border-dark-600 last:border-b-0 transition-colors"
              >
                <div className="flex items-start">
                  <MapPinIcon className="h-4 w-4 text-dark-400 mt-1 mr-2 flex-shrink-0" />
                  <div className="text-sm">
                    <div className="font-medium text-white truncate">
                      {result.address.street || 'Unknown Street'}
                    </div>
                    <div className="text-dark-300 truncate">
                      {[result.address.city, result.address.state, result.address.country]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map Container */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-dark-200">
          Company Location & Geofence
        </label>
        <div 
          ref={mapRef} 
          className={`w-full h-96 rounded-lg border border-dark-600 ${disabled ? 'opacity-50' : ''}`}
          style={{ minHeight: '384px' }}
        />
        
        {!isMapReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-800 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-2"></div>
              <p className="text-sm text-dark-300">Loading map...</p>
            </div>
          </div>
        )}
      </div>

      {/* Radius Control */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-dark-200 flex items-center">
          <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
          Geofence Radius: {currentRadius} meters
        </label>
        
        <div className="flex items-center space-x-4">
          <span className="text-sm text-dark-300">10m</span>
          <input
            type="range"
            min="10"
            max="1000"
            step="10"
            value={currentRadius}
            onChange={(e) => handleRadiusChange(parseInt(e.target.value))}
            disabled={disabled}
            className="flex-1 h-2 bg-dark-600 rounded-lg appearance-none cursor-pointer slider"
          />
          <span className="text-sm text-dark-300">1000m</span>
        </div>

        <div className="text-xs text-dark-400">
          Employees can only clock in when within this radius of the company location.
        </div>
      </div>

      {/* Current Coordinates Display */}
      <div className="bg-dark-800 p-3 rounded-lg border border-dark-600">
        <div className="text-sm font-medium text-dark-200 mb-1">Current Coordinates</div>
        <div className="grid grid-cols-2 gap-4 text-sm text-dark-300">
          <div>
            <span className="font-medium">Latitude:</span> {currentLocation.lat.toFixed(6)}
          </div>
          <div>
            <span className="font-medium">Longitude:</span> {currentLocation.lng.toFixed(6)}
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3B82F6;
          cursor: pointer;
          border: 2px solid #1F2937;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3B82F6;
          cursor: pointer;
          border: 2px solid #1F2937;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .slider:disabled::-webkit-slider-thumb {
          background: #6B7280;
          cursor: not-allowed;
        }

        .slider:disabled::-moz-range-thumb {
          background: #6B7280;
          cursor: not-allowed;
        }

        .custom-map-marker {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};

export default GeofenceMapPicker;